// Desired versus observed, and the one word that names the gap between them.
//
// The Control Plane owns what SHOULD be served. The serving tier answers with
// what IS served. Those are two different facts and the console must never
// print one while implying the other — an operator who clicked Publish has
// evidence that a revision was recorded, not evidence that anything serves it.
//
// The rule that shapes every branch below: NEVER REPORT AGREEMENT WE DID NOT
// OBSERVE. An observation that failed, or that has not been made yet, is
// UNKNOWN — never IN SYNC. A green state has to be earned by a document coming
// back with a matching revision, because the entire value of this comparison
// is that it can say no.

import type { StatusTone } from "./styles";
import type { Observation } from "./observed";

export type SyncStatus = "IN_SYNC" | "OUT_OF_SYNC" | "NOT_PUBLISHED" | "UNKNOWN";

export interface SyncState {
  status: SyncStatus;
  tone: StatusTone;
  /** One sentence an operator can act on, naming both sides of the comparison. */
  detail: string;
}

export function syncLabel(status: SyncStatus): string {
  return status.replace(/_/g, " ");
}

/**
 * Compare the revision the registry points at against the revision the tier
 * actually served.
 *
 * `desiredRevisionId` is `ProductDetailResponse.current.revisionId` — the
 * pointer, not the draft and not the newest revision in history. `observed` is
 * `null` before the first observation resolves.
 */
export function syncState(
  desiredRevisionId: string | null,
  observed: Observation | null,
): SyncState {
  if (observed === null) {
    return {
      status: "UNKNOWN",
      tone: "muted",
      detail: "The serving tier has not been asked yet.",
    };
  }

  if (observed.kind === "unavailable") {
    return {
      status: "UNKNOWN",
      tone: "warn",
      // Deliberately not DEGRADED or DOWN. This console cannot distinguish a
      // failing tier from a failing network path to it, and naming a cause it
      // cannot establish would be the same class of error as the claim this
      // module replaces.
      detail: `The serving tier could not be read: ${observed.detail}`,
    };
  }

  if (observed.kind === "not_serving") {
    if (desiredRevisionId === null) {
      return {
        status: "NOT_PUBLISHED",
        tone: "muted",
        detail: `Nothing is published and nothing is served — ${observed.reason}.`,
      };
    }
    return {
      status: "OUT_OF_SYNC",
      tone: "danger",
      detail:
        `The pointer names ${desiredRevisionId}, but the tier serves no ` +
        `document — ${observed.reason}.`,
    };
  }

  if (desiredRevisionId === null) {
    // The registry says this product has no current revision while the tier
    // hands out a document. Whatever the cause, the two authorities disagree,
    // and an operator reading "not published" here would be misled twice over.
    return {
      status: "OUT_OF_SYNC",
      tone: "danger",
      detail:
        `The registry holds no current revision, but the tier is serving ` +
        `${observed.revision}.`,
    };
  }

  if (observed.revision === desiredRevisionId) {
    return {
      status: "IN_SYNC",
      tone: "ok",
      detail: `The tier is serving ${observed.revision}, which is the current pointer.`,
    };
  }

  return {
    status: "OUT_OF_SYNC",
    tone: "danger",
    detail:
      `The pointer names ${desiredRevisionId}, but the tier is serving ` +
      `${observed.revision}. A publish or rollback within the last five ` +
      `minutes may still be propagating.`,
  };
}
